import { create } from 'zustand';
import type { StatusBarSurface } from '../lib/statusBarStyle';

interface StatusBarClaimModel {
	id: number;
	surface: StatusBarSurface;
}

interface StatusBarSurfaceState {
	/** Active claims, oldest first. The newest one decides the surface. */
	claims: Array<StatusBarClaimModel>;
	/** Record a claim and return its id, to hand back to `release`. */
	claim: (surface: StatusBarSurface) => number;
	/** Drop one claim. Unknown or already released ids are ignored. */
	release: (id: number) => void;
}

let nextClaimId = 1;

/**
 * Which surface sits under the native status bar (PD-16744), so the status
 * bar's text can follow the page instead of always following the header band.
 *
 * Pages claim a surface while mounted (`useStatusBarSurface`) and release
 * ONLY their own claim when they unmount. A route change mounts the next page
 * and unmounts the previous one in either order, so a plain "set / reset to
 * the default" would let the leaving page wipe the arriving page's value.
 * With no claims the header band is assumed, which is what every PortalLayout
 * page shows.
 *
 * BrandThemeApplier reads the current surface and remains the only caller of
 * the Capacitor status-bar plugin.
 */
export const useStatusBarSurfaceStore = create<StatusBarSurfaceState>(
	(set) => ({
		claims: [],
		claim: (surface) => {
			const id = nextClaimId;
			nextClaimId += 1;
			set((state) => ({ claims: [...state.claims, { id, surface }] }));
			return id;
		},
		release: (id) => {
			set((state) => {
				if (!state.claims.some((c) => c.id === id)) {
					return state;
				}
				return { claims: state.claims.filter((c) => c.id !== id) };
			});
		},
	}),
);

/** The surface the newest claim names, or `header` when nothing has claimed. */
export function selectStatusBarSurface(
	state: Pick<StatusBarSurfaceState, 'claims'>,
): StatusBarSurface {
	const newest = state.claims[state.claims.length - 1];
	return newest == null ? 'header' : newest.surface;
}
